/**
 * Gmail-style SCHEDULE SEND for the universal input box's email creation mode (busy-my-web#158,
 * Figma 40008595-224771 frames "Schedule send 1-4", "Schedule sending 1-2", "click undo 3").
 *
 * THE BACKEND GAP IS CLOSED (busy-main-bff#14). This module used to save the composed mail as a
 * DRAFT and keep the chosen moment as client-side metadata, because nothing in the platform could
 * hold a send on a clock — the whole flow was marked `pending` and carried a loud
 * `SCHEDULED_SEND_BACKEND_GAP` string so no reader could mistake it for a delivery. That is gone,
 * along with the string: a scheduled send is now a real send that happens later.
 *
 *   - `POST /v1/email` takes `sendAtUtc` (camelCase on the wire; `SendEmailDataDto.SendAtUtc` on
 *     the BFF). A FUTURE moment parks the mail in a Redis sorted set and answers with the schedule
 *     record `{ id, subject, sendAt, recipients }`. Null or an already-past moment falls through to
 *     the fire-immediately queue exactly as before, so the field is additive for every other caller.
 *   - `GET /v1/email/scheduled` lists the caller's waiting mail, soonest first.
 *   - `DELETE /v1/email/scheduled/{id}` removes one entirely — no draft, no trace, nothing left to
 *     send. That is what Undo cancels by, which is why the `id` is the one thing this module refuses
 *     to return without.
 *
 * `sendAtUtc` is NOT `DueDate`: that field is the email's waiting-on marker and has nothing to do
 * with delivery. Sending them as the same thing was the original mistake this flow had to avoid.
 */

/**
 * A readable moment, or null. Guards the two ways `new Date()` lies about nothing: `new Date(null)`
 * and `new Date('')` both land on the epoch rather than on Invalid Date.
 */
function toMoment (value) {
  if (value === null || value === undefined || value === '') return null
  const d = value instanceof Date ? value : new Date(value)
  return isNaN(d.getTime()) ? null : d
}

/** "12:30 PM" — the frames' clock, uppercase meridiem. */
export function scheduleClock (value) {
  // `new Date(null)` is the EPOCH, not an invalid date — an unset time would otherwise be written
  // out as a January 1970 moment. Nothing in means nothing out.
  const d = toMoment(value)
  if (!d) return ''
  let h = d.getHours()
  const meridiem = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${h}:${String(d.getMinutes()).padStart(2, '0')} ${meridiem}`
}

/** "Jul 22" — the frames' date, no year (the picker header carries the year). */
export function scheduleDay (value) {
  // `new Date(null)` is the EPOCH, not an invalid date — an unset time would otherwise be written
  // out as a January 1970 moment. Nothing in means nothing out.
  const d = toMoment(value)
  if (!d) return ''
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

/**
 * The briefing fact, frame 40008976:417412: "Send Jul 22, at 12:30 PM".
 * The comma is the frame's own — it sits after the day, not after the verb.
 */
export function scheduleFactText (value) {
  const day = scheduleDay(value)
  const clock = scheduleClock(value)
  if (!day || !clock) return ''
  return `Send ${day}, at ${clock}`
}

/** The in-card progress strip, frame 40008976:419174. */
export function schedulingText (value) {
  const day = scheduleDay(value)
  const clock = scheduleClock(value)
  if (!day || !clock) return ''
  return `Scheduling message for ${day} at ${clock}`
}

/** The green notification, frame 40008976:419800. */
export function scheduledNoticeText (value) {
  const day = scheduleDay(value)
  const clock = scheduleClock(value)
  if (!day || !clock) return ''
  return `Send scheduled for ${day} at ${clock}`
}

/** The indigo strip after Undo, frame 40008976:420415. */
export const REVERTED_TO_DRAFT_TEXT = 'Scheduled send canceled and message reverted to draft.'

/** The amber confirm's question, frame 40008976:417412. */
export const EMPTY_CONFIRM_TEXT = 'Send this message without a subject or text in the body?'

/**
 * Fatemeh's rule (issue #158): the confirm appears only when the email has NEITHER a subject NOR
 * any body text. A subject alone, or a body alone, schedules straight away.
 *
 * `body` arrives as the composer's HTML, so tags are stripped before asking whether anything was
 * typed — an empty ProseMirror document is `<p></p>`, which is not a message.
 */
export function needsEmptyConfirm ({ subject = '', body = '' } = {}) {
  const hasSubject = String(subject ?? '').trim().length > 0
  const hasBody = String(body ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .trim().length > 0
  return !hasSubject && !hasBody
}

/**
 * A scheduled time must be in the FUTURE. The picker can hand back today at a time that has already
 * passed (its day grid and its time list are independent), and "schedule" for a moment in the past
 * is just "send now" wearing the wrong word.
 */
export function isSchedulable (value, now = new Date()) {
  const d = toMoment(value)
  if (!d) return false
  return d.getTime() > (now instanceof Date ? now : new Date(now)).getTime()
}

/**
 * How far ahead the EARLIEST offer sits (busy-my-web#193). Gmail's rule, which is the one Fatemeh
 * asked for: the soonest a message can be scheduled is one hour after it is being written. Past
 * moments are the invariant — they are never selectable — and this lead is the adjustable part.
 */
export const SCHEDULE_LEAD_MS = 60 * 60 * 1000

/** The floor the schedule-send picker opens with: now + the lead. */
export function earliestScheduleTime (now = new Date()) {
  const base = now instanceof Date ? now : new Date(now)
  return new Date(base.getTime() + SCHEDULE_LEAD_MS)
}

/**
 * THE SEND. Hands the composed email to `POST /v1/email` with the chosen moment on it, and returns
 * the schedule record the box keeps so it can offer Undo and View.
 *
 * @param payload the email payload the box already builds (buildEmailPayload)
 * @param at      the chosen delivery time (Date or ISO string)
 * @param send    async (payload) => the server's schedule record — `email/scheduleSend`
 * @returns { id, at, sendAt, subject, recipients, payload }
 *
 * The `id` is load-bearing: it is the ONLY handle on a message that has left the composer and is
 * now the server's to deliver, so a response without one is treated as a failure rather than
 * reported as a success the user cannot take back.
 */
export async function deliverScheduledSend ({ payload, at, send } = {}) {
  const when = toMoment(at)
  if (!isSchedulable(when)) throw new Error('A scheduled send needs a time in the future.')

  // `sendAtUtc`, camelCase, alongside the payload the immediate send already takes — the field is
  // what splits the two paths on the BFF, so there is one send route and no second shape to keep.
  const scheduled = typeof send === 'function'
    ? await send({ ...payload, sendAtUtc: when.toISOString() })
    : null

  const id = scheduled?.id ?? null
  if (!id) throw new Error('The message was not scheduled — the server returned no schedule id.')

  return {
    id,
    at: when.toISOString(),
    sendAt: scheduled.sendAt ?? when.toISOString(),
    subject: scheduled.subject ?? null,
    recipients: scheduled.recipients ?? [],
    payload,
  }
}

/**
 * UNDO, before the moment: `DELETE /v1/email/scheduled/{id}` takes the message off the clock
 * entirely. A 404 means it is no longer waiting — already sent, or already cancelled — and the
 * caller must NOT report a cancellation that did not happen, so this answers false rather than
 * swallowing it.
 */
export async function cancelScheduledSend ({ id, cancel } = {}) {
  if (!id || typeof cancel !== 'function') return false
  try {
    await cancel(id)
    return true
  } catch {
    return false
  }
}

/**
 * Is this schedule still waiting? "View message" asks the scheduled list rather than trusting the
 * record the box is holding, because between the notification and the click the moment can arrive
 * and the mail can go — and reopening a message that has already been sent would invite the user to
 * edit and send a second copy.
 */
export function isStillScheduled (id, scheduledList = []) {
  if (!id) return false
  return (scheduledList || []).some(entry => entry?.id === id)
}

/**
 * WHERE "View" goes after a scheduled send (busy-my-web#442).
 *
 * Fatemeh, 2026-09-14: "when I send an email with scheduling and click View after sending, I should
 * no longer be taken back to the universal input box — I should go to the email itself", and then,
 * asked whether the folder was close enough: "it should open as an email and the scheduling message
 * under it, that's all".
 *
 * So View opens the MESSAGE, not the folder that holds it: `/emails/scheduled/:id`, the reader for
 * mail still waiting on the clock (views/emails/ScheduledMessage.vue). A queued send has no thread
 * of its own — nothing has been delivered, so there is no `/emails/timeline/:id` — which is why it
 * has a page of its own rather than reusing the thread reader; what it draws is the message as an
 * email (from, to, subject, body) with its delivery moment stated under it.
 *
 * The id goes in the PATH rather than in a `?open=` query: the page is about one message, and the
 * query form said "the folder, pointed at a row", which is the thing this replaced.
 *
 * Without an id there is still somewhere true to go: the folder itself.
 */
export function scheduledMessageRoute (id) {
  const key = String(id ?? '').trim()
  return key ? { path: `/emails/scheduled/${encodeURIComponent(key)}` } : { path: '/emails/scheduled' }
}

export default deliverScheduledSend
